import React, { useState, useRef, useEffect } from 'react';
import { X, Mail, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  redirectTo?: string;
  defaultTab?: MainTab;
}

type MainTab = 'login' | 'register' | 'forgot';
type RegMethod = 'email' | 'phone';
type ForgotMode = 'email' | 'phone';

// パスワードポリシー：8文字以上、大小文字・数字・記号を各1つ以上、空白禁止
const pwRe = /^(?!.*\s)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/;

// ログイン入力がメールかどうかを判定
const isEmailInput = (v: string) => v.includes('@');

// 60秒カウントダウン hook
function useCountdown(init = 0) {
  const [sec, setSec] = useState(init);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const start = (s: number) => {
    setSec(s);
    timerRef.current = setInterval(() => {
      setSec((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);
  return { sec, start };
}

// localStorage から記憶済みログイン情報を読み込む
const _savedRemember = localStorage.getItem('lp_remember_me') === 'true';
const _savedId = _savedRemember ? (localStorage.getItem('lp_remembered_id') || '') : '';
const _savedPw = _savedRemember ? (localStorage.getItem('lp_remembered_pw') || '') : '';

const EmailAuthModal: React.FC<Props> = ({ isOpen, onClose, redirectTo = '/mypage', defaultTab }) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  // メインタブ
  const [tab, setTab] = useState<MainTab>(defaultTab ?? 'login');

  // defaultTab 変更時に同期
  useEffect(() => { if (defaultTab) setTab(defaultTab); }, [defaultTab]);
  const [regMethod, setRegMethod] = useState<RegMethod>('email');

  // ログインフォーム
  const [loginId, setLoginId] = useState(_savedId);
  const [loginPw, setLoginPw] = useState(_savedPw);
  const [rememberMe, setRememberMe] = useState(_savedRemember);

  // メール登録フォーム
  const [regEmail, setRegEmail] = useState('');
  const [regPw, setRegPw] = useState('');
  const [regPw2, setRegPw2] = useState('');

  // 手機登録フォーム
  const [regPhone, setRegPhone] = useState('');
  const [regCode, setRegCode] = useState('');
  const [regPhonePw, setRegPhonePw] = useState('');
  const [regPhonePw2, setRegPhonePw2] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const { sec: countdown, start: startCountdown } = useCountdown();

  // パスワードリセットフォーム
  const [forgotMode, setForgotMode] = useState<ForgotMode>('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailDone, setForgotEmailDone] = useState(false);
  const [forgotPhone, setForgotPhone] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPw, setForgotNewPw] = useState('');
  const [forgotNewPw2, setForgotNewPw2] = useState('');
  const [forgotCodeSent, setForgotCodeSent] = useState(false);
  const { sec: forgotSec, start: startForgotCountdown } = useCountdown();
  const [forgotPhoneDone, setForgotPhoneDone] = useState(false);

  // メール認証コードステップ（登録成功後、LP を離れずにコード入力）
  const { sec: emailCodeSec, start: startEmailCodeCountdown } = useCountdown();
  const [showEmailCodeStep, setShowEmailCodeStep] = useState(false);
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');

  // 共通
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // メール未検証でログインしたときの再送
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendOk, setResendOk] = useState(false);

  // モーダルを閉じたときにコードステップをリセット
  useEffect(() => {
    if (!isOpen) {
      setShowEmailCodeStep(false);
      setEmailCode('');
      setPendingVerifyEmail('');
      setErr(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // ---- バリデーション ----
  const loginOk = !!loginId && !!loginPw && !loading;
  const emailRegOkBtn =
    !!regEmail && pwRe.test(regPw) && regPw === regPw2 && !loading;
  const phoneRegOkBtn =
    !!regPhone && !!regCode && pwRe.test(regPhonePw) && regPhonePw === regPhonePw2 && !loading;
  const forgotPhoneOkBtn =
    !!forgotPhone && forgotCodeSent && !!forgotCode &&
    pwRe.test(forgotNewPw) && forgotNewPw === forgotNewPw2 && !loading;

  // ---- タブ切り替え（全状態リセット） ----
  const switchTab = (next: MainTab) => {
    setShowEmailCodeStep(false);
    setEmailCode('');
    setTab(next);
    setErr(null);
    setUnverifiedEmail(null);
    if (next !== 'forgot') {
      setForgotEmailDone(false);
      setForgotPhoneDone(false);
      setForgotMode('email');
      setForgotEmail('');
      setForgotPhone('');
      setForgotCode('');
      setForgotNewPw('');
      setForgotNewPw2('');
      setForgotCodeSent(false);
    }
  };

  const switchRegMethod = (next: RegMethod) => {
    setRegMethod(next);
    setErr(null);
    setCodeSent(false);
  };

  // ---- 登録SMS送信 ----
  const sendCode = async () => {
    if (!regPhone) { setErr(t('auth.error_phone_required')); return; }
    setErr(null);
    try {
      await api.post('/auth/sms/request', { phone_number: regPhone });
      setCodeSent(true);
      startCountdown(60);
    } catch (e: any) {
      const code = e?.response?.data?.code;
      if (code === 'RATE_LIMIT_EXCEEDED') {
        const secs = e?.response?.data?.seconds || 60;
        setErr(t('auth.rate_limit_exceeded', { seconds: secs }));
        startCountdown(secs);
      } else {
        setErr(e?.response?.data?.message || t('common.error') || 'エラーが発生しました');
      }
    }
  };

  // ---- ログイン ----
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setUnverifiedEmail(null);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (isEmailInput(loginId)) {
        params.append('email', loginId.trim().toLowerCase());
      } else {
        params.append('phone_number', loginId.trim());
      }
      params.append('password', loginPw);
      const res = await api.post('/auth/login/user', params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      // 記憶するかどうかに応じて localStorage を更新
      if (rememberMe) {
        localStorage.setItem('lp_remember_me', 'true');
        localStorage.setItem('lp_remembered_id', loginId);
        localStorage.setItem('lp_remembered_pw', loginPw);
      } else {
        localStorage.removeItem('lp_remember_me');
        localStorage.removeItem('lp_remembered_id');
        localStorage.removeItem('lp_remembered_pw');
      }
      login(res.data.access_token, res.data.role || 'user', 'email');
      onClose();
      navigate(redirectTo);
    } catch (error: any) {
      const code = error?.response?.data?.code;
      if (code === 'EMAIL_NOT_FOUND') setErr(t('auth.email_not_found'));
      else if (code === 'PHONE_NOT_FOUND') setErr(t('auth.phone_not_found'));
      else if (code === 'INCORRECT_PASSWORD') setErr(t('auth.incorrect_password'));
      else if (code === 'ACCOUNT_BANNED') setErr(t('auth.account_banned'));
      else if (code === 'EMAIL_NOT_VERIFIED') {
        // メール未検証：再送ボタンを表示する
        setErr(t('auth.email_not_verified') || 'メール認証が未完了です / 请先验证邮箱');
        if (isEmailInput(loginId)) setUnverifiedEmail(loginId.trim().toLowerCase());
      } else {
        setErr(error?.response?.data?.message || t('common.error') || 'エラーが発生しました');
      }
    } finally {
      setLoading(false);
    }
  };

  // ---- メール登録 ----
  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const addr = regEmail.trim().toLowerCase();
      const pendingRef = sessionStorage.getItem('pending_ref_token');
      await api.post(
        pendingRef ? `/auth/register/email?ref=${encodeURIComponent(pendingRef)}` : '/auth/register/email',
        { email: addr, password: regPw },
      );
      // 登録成功 → LP を離れずにコード入力ステップへ
      setPendingVerifyEmail(addr);
      setShowEmailCodeStep(true);
      startEmailCodeCountdown(60);
      setErr(null);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const code = typeof detail === 'object' ? detail?.code : error?.response?.data?.code;
      if (code === 'EMAIL_ALREADY_REGISTERED') {
        setErr(t('auth.email_already_registered'));
      } else {
        setErr(error?.response?.data?.message || t('auth.error_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // ---- 未認証ログイン時の再送 → コードを送ってコードステップへ ----
  const resendVerification = async (email: string) => {
    setResending(true);
    setResendOk(false);
    try {
      await api.post('/auth/email/code/resend', { email });
      setPendingVerifyEmail(email);
      setShowEmailCodeStep(true);
      startEmailCodeCountdown(60);
      setErr(null);
    } catch (e: any) {
      const code = e?.response?.data?.code;
      if (code === 'RATE_LIMIT_EXCEEDED') {
        // レート制限中でもコードは存在するのでステップへ
        setPendingVerifyEmail(email);
        setShowEmailCodeStep(true);
        startEmailCodeCountdown(e?.response?.data?.seconds || 60);
        setErr(null);
      } else {
        setResendOk(true); // 秘匿的
      }
    } finally {
      setResending(false);
    }
  };

  // ---- メール認証コード検証 → 即時ログイン ----
  const handleEmailCodeVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const res = await api.post('/auth/email/code/verify', {
        email: pendingVerifyEmail,
        code: emailCode.trim(),
      });
      if (res.data.access_token) {
        sessionStorage.removeItem('pending_ref_token');
        login(res.data.access_token, res.data.role || 'user', 'email');
        onClose();
        if (res.data.is_new_user) {
          navigate('/welcome');
        } else {
          navigate(redirectTo);
        }
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const errCode = typeof detail === 'object' ? detail?.code : null;
      if (errCode === 'CODE_EXPIRED') setErr(t('auth.code_expired') || '認証コードの有効期限が切れました。再送してください。');
      else if (errCode === 'INVALID_CODE') setErr(t('auth.invalid_code') || '認証コードが正しくありません。');
      else setErr(error?.response?.data?.message || t('common.error') || 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // ---- メール認証コード再送 ----
  const handleEmailCodeResend = async () => {
    setErr(null);
    try {
      await api.post('/auth/email/code/resend', { email: pendingVerifyEmail });
      startEmailCodeCountdown(60);
    } catch (e: any) {
      const code = e?.response?.data?.code;
      if (code === 'RATE_LIMIT_EXCEEDED') {
        startEmailCodeCountdown(e?.response?.data?.seconds || 60);
      } else {
        setErr(e?.response?.data?.message || t('common.error') || 'エラーが発生しました');
      }
    }
  };

  // ---- 手機登録 ----
  const handlePhoneRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const pendingRef = sessionStorage.getItem('pending_ref_token');
      const res = await api.post(
        pendingRef ? `/auth/register?ref=${encodeURIComponent(pendingRef)}` : '/auth/register',
        {
          phone_number: regPhone.trim(),
          verification_code: regCode.trim(),
          password: regPhonePw,
          provider: 'phone',
        },
      );
      // 手機登録は自動ログイン（access_token が返る）
      if (res.data?.access_token) {
        login(res.data.access_token, res.data.role || 'user', 'phone');
        onClose();
        navigate(redirectTo);
      }
    } catch (error: any) {
      const code = error?.response?.data?.code;
      const msg = error?.response?.data?.message;
      if (code === 'PHONE_ALREADY_REGISTERED') setErr(t('auth.phone_already_registered'));
      else if (msg?.toLowerCase().includes('expired')) setErr(t('auth.code_expired'));
      else if (msg?.toLowerCase().includes('invalid')) setErr(t('auth.invalid_code'));
      else setErr(msg || t('auth.error_failed'));
    } finally {
      setLoading(false);
    }
  };

  // ---- パスワードリセット：メールでリンク送信 ----
  const handleEmailForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await api.post('/auth/password/forgot', { email: forgotEmail.trim().toLowerCase() });
      setForgotEmailDone(true);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status && status >= 500) {
        setErr(t('auth.error_network'));
      } else {
        // セキュリティのため、存在しないメールでも成功扱い
        setForgotEmailDone(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // ---- パスワードリセット：SMS送信 ----
  const sendForgotCode = async () => {
    if (!forgotPhone) { setErr(t('auth.error_phone_required')); return; }
    setErr(null);
    try {
      await api.post('/auth/sms/request', { phone_number: forgotPhone.trim() });
      setForgotCodeSent(true);
      startForgotCountdown(60);
    } catch (e: any) {
      const code = e?.response?.data?.code;
      if (code === 'RATE_LIMIT_EXCEEDED') {
        const secs = e?.response?.data?.seconds || 60;
        setErr(t('auth.rate_limit_exceeded', { seconds: secs }));
        startForgotCountdown(secs);
      } else {
        setErr(e?.response?.data?.message || t('common.error') || 'エラーが発生しました');
      }
    }
  };

  // ---- パスワードリセット：電話番号で再設定 ----
  const handlePhoneForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pwRe.test(forgotNewPw)) { setErr(t('auth.error_password_policy')); return; }
    if (forgotNewPw !== forgotNewPw2) { setErr(t('auth.error_password_mismatch')); return; }
    setErr(null);
    setLoading(true);
    try {
      await api.post('/auth/password/reset/phone', {
        phone_number: forgotPhone.trim(),
        code: forgotCode.trim(),
        new_password: forgotNewPw,
      });
      setForgotPhoneDone(true);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.response?.data?.message || t('auth.error_failed');
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  // ---- 共通 input クラス ----
  const inputCls = 'w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-coral/30';

  return (
    <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-50 w-full max-w-sm rounded-3xl bg-white shadow-elevated animate-in zoom-in-95 duration-200 max-h-[calc(100vh-2rem)] overflow-y-auto">

        {/* ヘッダー */}
        <div className="flex items-center justify-between px-6 pt-6 pb-0 sticky top-0 bg-white rounded-t-3xl z-10">
          <div className="flex items-center gap-2">
            {(tab === 'forgot' || showEmailCodeStep) ? (
              <button
                onClick={() => {
                  if (showEmailCodeStep) { setShowEmailCodeStep(false); setEmailCode(''); setErr(null); }
                  else switchTab('login');
                }}
                className="rounded-full p-1 text-slate-400 hover:bg-slate-100 transition-colors"
                aria-label="back"
              >
                <ArrowLeft size={18} />
              </button>
            ) : (
              <Mail className="w-5 h-5 text-coral shrink-0" />
            )}
            <h3 className="text-base font-bold text-navy">
              {showEmailCodeStep
                ? t('auth.email_verification')
                : tab === 'forgot'
                  ? t('auth.forgot_password')
                  : tab === 'login'
                    ? t('auth.login_title')
                    : t('auth.register_title')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* メインタブ（forgot / コードステップ表示中は非表示） */}
        {tab !== 'forgot' && !showEmailCodeStep && (
          <div className="flex mx-6 mt-4 rounded-xl bg-slate-100 p-1 gap-1">
            {(['login', 'register'] as MainTab[]).map((t_) => (
              <button
                key={t_}
                onClick={() => switchTab(t_)}
                className={classNames(
                  'flex-1 py-1.5 text-sm font-semibold rounded-lg transition-all',
                  tab === t_ ? 'bg-white text-navy shadow-sm' : 'text-slate-500 hover:text-navy'
                )}
              >
                {t_ === 'login' ? t('auth.login_title') : t('auth.register_title')}
              </button>
            ))}
          </div>
        )}

        <div className="px-6 pt-4 pb-6">

          {/* ===== メール認証コードステップ ===== */}
          {showEmailCodeStep && (
            <form onSubmit={handleEmailCodeVerify} className="space-y-4">
              <div className="text-center py-2">
                <div className="text-4xl mb-3">📧</div>
                <p className="text-sm text-slate-500 leading-relaxed">
                  <span className="font-semibold text-navy">{pendingVerifyEmail}</span>
                  {t('auth.email_code_desc')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-center">{t('auth.verification_code')}</label>
                <div
                  className="relative flex gap-2 justify-center cursor-text"
                  onClick={() => (document.getElementById('email-code-input') as HTMLInputElement)?.focus()}
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className={classNames(
                        'w-10 h-12 border-2 rounded-xl flex items-center justify-center text-xl font-bold transition-all select-none',
                        emailCode[i]
                          ? 'border-coral bg-coral/5 text-navy'
                          : emailCode.length === i
                            ? 'border-coral'
                            : 'border-gray-200'
                      )}
                    >
                      {emailCode[i] || (emailCode.length === i
                        ? <span className="animate-pulse text-coral text-base leading-none">|</span>
                        : '')}
                    </div>
                  ))}
                  <input
                    id="email-code-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="one-time-code"
                    className="absolute inset-0 opacity-0 cursor-text w-full"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                    required
                  />
                </div>
              </div>
              {err && <p className="text-sm text-red-600">{err}</p>}
              <button
                type="submit"
                disabled={emailCode.length !== 6 || loading}
                className={classNames(
                  'w-full py-3 rounded-xl text-sm font-bold text-white transition-all',
                  emailCode.length === 6 && !loading
                    ? 'bg-coral hover:opacity-90 active:scale-[0.98]'
                    : 'bg-slate-300 cursor-not-allowed'
                )}
              >
                {loading ? (t('auth.logging_in') || '...') : t('auth.verify_code_btn')}
              </button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleEmailCodeResend}
                  disabled={emailCodeSec > 0}
                  className="text-xs text-slate-400 hover:text-coral transition-colors disabled:cursor-not-allowed"
                >
                  {emailCodeSec > 0
                    ? t('auth.resend_in', { seconds: emailCodeSec })
                    : (t('auth.resend_code') || '再送')}
                </button>
              </div>
            </form>
          )}

          {/* ===== ログインフォーム ===== */}
          {!showEmailCodeStep && tab === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('auth.email')}
                </label>
                <input
                  type="text"
                  autoComplete="username"
                  placeholder={t('auth.email')}
                  className={inputCls}
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('auth.password')}</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  className={inputCls}
                  value={loginPw}
                  onChange={(e) => setLoginPw(e.target.value)}
                  required
                />
              </div>

              {/* 記憶するチェックボックス */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 accent-coral rounded"
                />
                <span className="text-xs text-slate-500">{t('auth.remember_me')}</span>
              </label>

              {err && (
                <div className="space-y-1">
                  <p className="text-sm text-red-600">{err}</p>
                  {/* メール未検証の場合は再送ボタンを表示 */}
                  {unverifiedEmail && (
                    <button
                      type="button"
                      onClick={() => resendVerification(unverifiedEmail)}
                      disabled={resending}
                      className="text-xs text-coral underline hover:opacity-70 transition"
                    >
                      {resending
                        ? (t('auth.resend_in', { seconds: '...' }) || '送信中...')
                        : (t('auth.resend_code') || '再送')}
                    </button>
                  )}
                  {resendOk && (
                    <p className="text-xs text-emerald-600">{t('auth.code_sent') || '送信しました'}</p>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={!loginOk}
                className={classNames(
                  'w-full py-3 rounded-xl text-sm font-bold text-white transition-all',
                  loginOk ? 'bg-coral hover:opacity-90 active:scale-[0.98]' : 'bg-slate-300 cursor-not-allowed'
                )}
              >
                {loading ? t('auth.logging_in') : t('auth.login_button')}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => switchTab('forgot')}
                  className="text-xs text-slate-400 hover:text-coral transition-colors"
                >
                  {t('auth.forgot_password')}
                </button>
              </div>
            </form>
          )}

          {/* ===== 登録フォーム ===== */}
          {!showEmailCodeStep && tab === 'register' && (
            <>
              {/* 登録方法サブタブ（手機登録は非表示） */}

              {regMethod === 'email' ? (
                /* ---- メール登録フォーム ---- */
                <form onSubmit={handleEmailRegister} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('auth.email')}</label>
                    <input
                      type="email"
                      autoComplete="email"
                      className={inputCls}
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('auth.password')}</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      className={inputCls}
                      value={regPw}
                      onChange={(e) => setRegPw(e.target.value)}
                      required
                    />
                    <p className="text-[11px] text-slate-400 mt-1">{t('login_success.password_rule_hint')}</p>
                    {regPw && !pwRe.test(regPw) && (
                      <p className="text-[11px] text-red-500 mt-0.5">{t('login_success.invalid_password')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('login_success.confirm_password_label')}</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      className={inputCls}
                      value={regPw2}
                      onChange={(e) => setRegPw2(e.target.value)}
                      required
                    />
                    {regPw2 && regPw !== regPw2 && (
                      <p className="text-[11px] text-red-500 mt-0.5">{t('login_success.password_mismatch')}</p>
                    )}
                  </div>
                  {err && <p className="text-sm text-red-600">{err}</p>}
                  <button
                    type="submit"
                    disabled={!emailRegOkBtn}
                    className={classNames(
                      'w-full py-3 rounded-xl text-sm font-bold text-white transition-all',
                      emailRegOkBtn ? 'bg-coral hover:opacity-90 active:scale-[0.98]' : 'bg-slate-300 cursor-not-allowed'
                    )}
                  >
                    {loading ? (t('auth.registering') || '...') : t('auth.register_button')}
                  </button>
                </form>
              ) : (
                /* ---- 手機登録フォーム ---- */
                <form onSubmit={handlePhoneRegister} className="space-y-4">
                  {/* 電話番号 + 送信ボタン */}
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('auth.phone_number')}</label>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        autoComplete="tel"
                        className={classNames(inputCls, 'flex-1')}
                        placeholder={t('auth.enter_phone') || '例: 090-1234-5678'}
                        value={regPhone}
                        onChange={(e) => setRegPhone(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={sendCode}
                        disabled={!regPhone || countdown > 0}
                        className={classNames(
                          'shrink-0 px-3 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap',
                          countdown > 0
                            ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                            : regPhone
                              ? 'bg-coral text-white hover:opacity-90'
                              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        )}
                      >
                        {countdown > 0
                          ? t('auth.resend_in', { seconds: countdown })
                          : (codeSent ? (t('auth.resend_code') || '再送') : (t('auth.send_code') || '送信'))}
                      </button>
                    </div>
                  </div>

                  {/* 認証コード */}
                  {codeSent && (
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('auth.verification_code')}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        autoComplete="one-time-code"
                        className={inputCls}
                        placeholder="------"
                        value={regCode}
                        onChange={(e) => setRegCode(e.target.value.replace(/\D/g, ''))}
                        required
                      />
                    </div>
                  )}

                  {/* パスワード */}
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('auth.password')}</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      className={inputCls}
                      value={regPhonePw}
                      onChange={(e) => setRegPhonePw(e.target.value)}
                      required
                    />
                    <p className="text-[11px] text-slate-400 mt-1">{t('login_success.password_rule_hint')}</p>
                    {regPhonePw && !pwRe.test(regPhonePw) && (
                      <p className="text-[11px] text-red-500 mt-0.5">{t('login_success.invalid_password')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('login_success.confirm_password_label')}</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      className={inputCls}
                      value={regPhonePw2}
                      onChange={(e) => setRegPhonePw2(e.target.value)}
                      required
                    />
                    {regPhonePw2 && regPhonePw !== regPhonePw2 && (
                      <p className="text-[11px] text-red-500 mt-0.5">{t('login_success.password_mismatch')}</p>
                    )}
                  </div>

                  {err && <p className="text-sm text-red-600">{err}</p>}

                  <button
                    type="submit"
                    disabled={!phoneRegOkBtn}
                    className={classNames(
                      'w-full py-3 rounded-xl text-sm font-bold text-white transition-all',
                      phoneRegOkBtn ? 'bg-coral hover:opacity-90 active:scale-[0.98]' : 'bg-slate-300 cursor-not-allowed'
                    )}
                  >
                    {loading ? (t('auth.registering') || '...') : t('auth.register_button')}
                  </button>
                </form>
              )}
            </>
          )}

          {/* ===== パスワードリセット ===== */}
          {!showEmailCodeStep && tab === 'forgot' && (
            <>
              {/* モード切替サブタブ（手機リセットは非表示） */}

              {forgotMode === 'email' ? (
                /* ---- メールでリセット ---- */
                forgotEmailDone ? (
                  <div className="text-center py-4 space-y-4">
                    <div className="text-4xl">📧</div>
                    <p className="text-sm text-navy font-medium">{t('auth.reset_link_sent')}</p>
                    <button
                      type="button"
                      onClick={() => setForgotEmailDone(false)}
                      className="text-xs text-coral underline hover:opacity-70 transition"
                    >
                      {t('auth.try_again')}
                    </button>
                    <button
                      onClick={() => switchTab('login')}
                      className="w-full py-3 rounded-xl bg-coral text-white text-sm font-bold hover:opacity-90"
                    >
                      {t('auth.back_to_login')}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleEmailForgot} className="space-y-4">
                    <p className="text-xs text-slate-500 leading-relaxed">{t('auth.forgot_password_desc')}</p>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('auth.email')}</label>
                      <input
                        type="email"
                        autoComplete="email"
                        className={inputCls}
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                      />
                    </div>
                    {err && <p className="text-sm text-red-600">{err}</p>}
                    <button
                      type="submit"
                      disabled={!forgotEmail || loading}
                      className={classNames(
                        'w-full py-3 rounded-xl text-sm font-bold text-white transition-all',
                        forgotEmail && !loading ? 'bg-coral hover:opacity-90 active:scale-[0.98]' : 'bg-slate-300 cursor-not-allowed'
                      )}
                    >
                      {loading ? t('auth.sending') : t('auth.send_reset_link')}
                    </button>
                  </form>
                )
              ) : (
                /* ---- 電話番号でリセット ---- */
                forgotPhoneDone ? (
                  <div className="text-center py-4 space-y-4">
                    <div className="text-4xl">✅</div>
                    <p className="text-sm text-navy font-medium">{t('auth.password_reset_success')}</p>
                    <button
                      onClick={() => switchTab('login')}
                      className="w-full py-3 rounded-xl bg-coral text-white text-sm font-bold hover:opacity-90"
                    >
                      {t('auth.back_to_login')}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handlePhoneForgot} className="space-y-4">
                    <p className="text-xs text-slate-500 leading-relaxed">{t('auth.phone_reset_desc')}</p>

                    {/* 電話番号 + SMS送信 */}
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('auth.phone_number')}</label>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          autoComplete="tel"
                          className={classNames(inputCls, 'flex-1')}
                          placeholder={t('auth.enter_phone') || '例: +81-90-1234-5678'}
                          value={forgotPhone}
                          onChange={(e) => setForgotPhone(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          onClick={sendForgotCode}
                          disabled={!forgotPhone || forgotSec > 0}
                          className={classNames(
                            'shrink-0 px-3 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap',
                            forgotSec > 0
                              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                              : forgotPhone
                                ? 'bg-coral text-white hover:opacity-90'
                                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                          )}
                        >
                          {forgotSec > 0
                            ? t('auth.resend_in', { seconds: forgotSec })
                            : (forgotCodeSent ? (t('auth.resend_code') || '再送') : (t('auth.send_code') || '送信'))}
                        </button>
                      </div>
                    </div>

                    {/* 認証コード */}
                    {forgotCodeSent && (
                      <div>
                        <label className="block text-sm font-medium mb-1">{t('auth.verification_code')}</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          autoComplete="one-time-code"
                          className={inputCls}
                          placeholder="------"
                          value={forgotCode}
                          onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ''))}
                          required
                        />
                      </div>
                    )}

                    {/* 新しいパスワード */}
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('auth.new_password')}</label>
                      <input
                        type="password"
                        autoComplete="new-password"
                        className={inputCls}
                        value={forgotNewPw}
                        onChange={(e) => setForgotNewPw(e.target.value)}
                        required
                      />
                      <p className="text-[11px] text-slate-400 mt-1">{t('login_success.password_rule_hint')}</p>
                      {forgotNewPw && !pwRe.test(forgotNewPw) && (
                        <p className="text-[11px] text-red-500 mt-0.5">{t('login_success.invalid_password')}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('auth.confirm_password')}</label>
                      <input
                        type="password"
                        autoComplete="new-password"
                        className={inputCls}
                        value={forgotNewPw2}
                        onChange={(e) => setForgotNewPw2(e.target.value)}
                        required
                      />
                      {forgotNewPw2 && forgotNewPw !== forgotNewPw2 && (
                        <p className="text-[11px] text-red-500 mt-0.5">{t('login_success.password_mismatch')}</p>
                      )}
                    </div>

                    {err && <p className="text-sm text-red-600">{err}</p>}

                    <button
                      type="submit"
                      disabled={!forgotPhoneOkBtn}
                      className={classNames(
                        'w-full py-3 rounded-xl text-sm font-bold text-white transition-all',
                        forgotPhoneOkBtn ? 'bg-coral hover:opacity-90 active:scale-[0.98]' : 'bg-slate-300 cursor-not-allowed'
                      )}
                    >
                      {loading ? (t('auth.resetting') || '...') : t('auth.reset_now')}
                    </button>
                  </form>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailAuthModal;
